import round from 'lodash/round';
import {
	coinFlip,
	mapRange,
	rotate,
	rotationTransforms,
	shouldBeCircle,
} from './utils';

const ROTATION_SPEED_MIN = 200; // minimum possible duration of single particle full rotation
const ROTATION_SPEED_MAX = 800; // maximum possible duration of single particle full rotation
const CRAZY_PARTICLES_FREQUENCY = 0.1; // 0-1 frequency of crazy curvy unpredictable particles
const CRAZY_PARTICLE_CRAZINESS = 0.25; // 0-1 how crazy these crazy particles are
const BEZIER_MEDIAN = 0.5; // utility for mid-point bezier curves, to ensure smooth motion paths

export interface IParticle {
	color: string; // color of particle
	degree: number; // vector direction, between 0-360 (0 being straight up ↑)
}

interface IParticlesProps {
	particles: IParticle[];
	duration: number;
	particleSize: number;
	force: number;
	width: number;
}

const generateKeyframes = (particles: IParticle[], width: number) => {
	// Generate rotation keyframes
	const rotationKeyframes = rotationTransforms
		.map(
			(xyz, i) => `
		@keyframes rotation-${i} {
			0% {
				transform: rotate3d(${xyz.join()}, 0deg);
			}
			50% {
				transform: rotate3d(${xyz.map((v) => v / 2).join()}, 180deg);
			}
			100% {
				transform: rotate3d(${xyz.join()}, 360deg);
			}
		}
	`
		)
		.join('\n');

	// Generate movement keyframes
	const movementKeyframes = particles
		.map((particle, i) => {
			const landingPoint = mapRange(
				Math.abs(rotate(particle.degree, 90) - 180),
				0,
				180,
				-width / 2,
				width / 2
			);

			return `
			@keyframes x-axis-${i} {
				0% {
					transform: translateX(0);
				}
				to {
					transform: translateX(${landingPoint}px);
				}
			}
		`;
		})
		.join('\n');

	return `
		${rotationKeyframes}
		@keyframes y-axis {
			0% {
				transform: translateY(0);
			}
			to {
				transform: translateY(100vh);
			}
		}
		${movementKeyframes}
	`;
};

export const generateStyles = ({
	particles,
	duration,
	particleSize,
	force,
	width,
}: IParticlesProps) => {
	const keyframes = generateKeyframes(particles, width);
	const particleStyles = particles
		.map((particle, i) => {
			const rotation = Math.round(
				Math.random() * (ROTATION_SPEED_MAX - ROTATION_SPEED_MIN) +
					ROTATION_SPEED_MIN
			);
			const rotationIndex = Math.round(
				Math.random() * (rotationTransforms.length - 1)
			);
			const durationChaos = duration - Math.round(Math.random() * 1000);
			const shouldBeCrazy = Math.random() < CRAZY_PARTICLES_FREQUENCY;
			const isCircle = shouldBeCircle(rotationIndex);

			// x-axis disturbance, roughly the distance the particle will initially deviate from its target
			const x1 = shouldBeCrazy
				? round(Math.random() * CRAZY_PARTICLE_CRAZINESS, 2)
				: 0;
			const x2 = x1 * -1;
			const x3 = x1;
			// x-axis arc of explosion, so 90deg and 270deg particles have curve of 1, 0deg and 180deg have 0
			const x4 = round(
				Math.abs(
					mapRange(Math.abs(rotate(particle.degree, 90) - 180), 0, 180, -1, 1)
				),
				4
			);

			// roughly how fast particle reaches end of its explosion curve
			const y1 = round(Math.random() * BEZIER_MEDIAN, 4);
			// roughly maps to the distance particle goes before reaching free-fall
			const y2 = round(Math.random() * force * (coinFlip() ? 1 : -1), 4);
			// roughly how soon the particle transitions from explosion to free-fall
			const y3 = BEZIER_MEDIAN;
			// roughly the ease of free-fall
			const y4 = round(
				Math.max(
					mapRange(Math.abs(particle.degree - 180), 0, 180, force, -force),
					0
				),
				4
			);

			return `
			.particle-${i} {
				position: absolute;
				left: 0;
				top: 0;
				animation: x-axis-${i} ${durationChaos}ms forwards cubic-bezier(${x1}, ${x2}, ${x3}, ${x4});
			}
			.particle-${i} > div {
				position: absolute;
				left: 0;
				top: 0;
				width: ${isCircle ? particleSize : Math.round(Math.random() * 4) + particleSize / 2}px;
				height: ${isCircle ? particleSize : Math.round(Math.random() * 2) + particleSize}px;
				animation: y-axis ${durationChaos}ms forwards cubic-bezier(${y1}, ${y2}, ${y3}, ${y4});
			}
			.particle-${i} > div:after {
				content: '';
				display: block;
				width: 100%;
				height: 100%;
				background-color: ${particle.color};
				border-radius: ${isCircle ? '50%' : '0'};
				animation: rotation-${rotationIndex} ${rotation}ms infinite linear;
			}
		`;
		})
		.join('\n');

	return `
		${keyframes}
		${particleStyles}
	`;
};
